// No imports, on purpose: `scripts/digital-card-backdrop-tones` loads this file by relative path under
// ts-node, which does not resolve the app's path aliases.

export const BACKDROP_BASE_URL = 'https://withjoy.com/media/paper-backdrop';

/**
 * The stock images a digital card can use as its backdrop: the area around the card, not the card's
 * own background. Each is `<style>-<name>`, the file's name under `paper-backdrop/`.
 */
export const BACKDROP_IDS = [
  'botanical-banana',
  'botanical-camellia',
  'botanical-ferns',
  'botanical-ficus',
  'botanical-laurel',
  'botanical-phoenix',
  'botanical-pothos',
  'celebration-fairylight',
  'celebration-fluted',
  'celebration-lametta',
  'celebration-mardigras',
  'celebration-mirrorball',
  'destination-alps',
  'destination-caustics',
  'destination-siusi',
  'floral-geranium',
  'floral-peonies',
  'floral-yarrow',
  'gradient-afterglow',
  'gradient-aura',
  'gradient-flare',
  'mosaic-arabesque',
  'mosaic-octagram',
  'textured-adobe',
  'textured-carrara',
  'textured-heartwood',
  'textured-impasto',
  'textured-sahara',
  'textured-satindrape',
  'textured-satinripple',
  'textured-satinswirl',
  'textured-slate',
  'textured-terrazzo',
  'textured-volcanic',
] as const;

export type BackdropTone = 'light' | 'dark';

/**
 * How light each backdrop is where the recipient page's footer sits over it: the bottom of the image,
 * across its middle. `light` takes dark text, `dark` takes light text.
 *
 * Suggested by `yarn card:digital-backdrop-tones`, which measures each image, then reviewed by eye -- an entry may be
 * overridden by hand when the measurement misreads a busy image; the script reports it as a difference
 * rather than overwriting it unless run with `--write`. Add an entry with every new id.
 */
export const BACKDROP_TONES: Readonly<Record<(typeof BACKDROP_IDS)[number], BackdropTone>> = {
  // @tones-start
  'botanical-banana': 'dark',
  'botanical-camellia': 'light',
  'botanical-ferns': 'dark',
  'botanical-ficus': 'dark',
  'botanical-laurel': 'dark',
  'botanical-phoenix': 'light',
  'botanical-pothos': 'light',
  'celebration-fairylight': 'dark',
  'celebration-fluted': 'light',
  'celebration-lametta': 'dark',
  'celebration-mardigras': 'dark',
  'celebration-mirrorball': 'dark',
  'destination-alps': 'dark',
  'destination-caustics': 'light',
  'destination-siusi': 'dark',
  'floral-geranium': 'light',
  'floral-peonies': 'dark',
  'floral-yarrow': 'light',
  'gradient-afterglow': 'dark',
  'gradient-aura': 'light',
  'gradient-flare': 'light',
  'mosaic-arabesque': 'light',
  'mosaic-octagram': 'light',
  'textured-adobe': 'light',
  'textured-carrara': 'light',
  'textured-heartwood': 'light',
  'textured-impasto': 'light',
  'textured-sahara': 'light',
  'textured-satindrape': 'light',
  'textured-satinripple': 'light',
  'textured-satinswirl': 'dark',
  'textured-slate': 'dark',
  'textured-terrazzo': 'light',
  'textured-volcanic': 'dark',
  // @tones-end
};
